// shared-components/ui/Button.jsx
import { forwardRef, memo } from "react";
import PropTypes from "prop-types";
import { cn } from "@shared/lib/utils";
import { THEME } from "@shared/constants/designSystem";
import { useSiteGradients } from "@shared/hooks";
import {
  getAllValidSizes,
  normalizeSize,
} from "@shared/constants/componentSizes";

// Move style configurations outside component
const BASE_STYLES =
  "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

const VARIANTS = {
  primary: `${THEME.COLORS.BG.PRIMARY} ${THEME.COLORS.TEXT.ON_PRIMARY} ${THEME.COLORS.HOVER.BG.PRIMARY}`,
  secondary: `${THEME.COLORS.BG.SECONDARY} ${THEME.COLORS.TEXT.ON_SECONDARY} ${THEME.COLORS.HOVER.BG.SECONDARY}`,
  outline: `border-2 ${THEME.COLORS.BORDER.DEFAULT} ${THEME.COLORS.TEXT.DEFAULT} hover:bg-surface-muted dark:hover:bg-surface-muted-dark`,
  ghost: `${THEME.COLORS.TEXT.DEFAULT} hover:bg-surface-muted dark:hover:bg-surface-muted-dark`,
};

const SIZES = {
  xs: "h-6 px-2 text-xs",
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 py-2", // default size
  lg: "h-12 px-4 sm:px-6 text-lg",
  xl: "h-14 px-6 sm:px-8 text-xl",
};

const Button = forwardRef(
  (
    {
      className,
      variant = "primary",
      size = "md",
      disabled = false,
      children,
      as: Component = "button", // Support for the 'as' prop
      gradient = false, // Add gradient prop
      gradientType = null, // Specific gradient to use
      animation = null, // Optional animation
      style = {},
      ...props
    },
    ref,
  ) => {
    // Always call hooks unconditionally
    const siteGradients = useSiteGradients();
    const gradients =
      gradient || gradientType || animation ? siteGradients : null;

    // Normalize size for backward compatibility
    const normalizedSize = normalizeSize(size);

    // Determine classes based on whether gradient is enabled
    let variantClasses;
    let animationClass = "";
    const buttonStyle = { ...style };

    if (gradient || gradientType) {
      // Use gradient instead of regular variant styling
      const gradientClass =
        gradientType && gradients[gradientType]
          ? gradients[gradientType]
          : variant === "secondary"
            ? gradients.buttonSecondary
            : variant === "outline"
              ? `border-2 ${THEME.COLORS.BORDER.DEFAULT} ${gradients.button}`
              : gradients.button;

      variantClasses = `${gradientClass} ${THEME.COLORS.TEXT.ON_PRIMARY}`;

      // Add animation if specified
      if (animation && gradients?.animations?.[animation]) {
        animationClass = gradients.animations[animation];

        // Add inline animation style for consistent behavior
        if (animation === "goldenPulse") {
          buttonStyle.animation =
            "pulse 1.618s cubic-bezier(0.618, 0, 0.382, 1) infinite";
        } else if (animation === "goldenSpin") {
          buttonStyle.animation = "spin 16.18s linear infinite";
        } else if (animation === "specialPulse") {
          buttonStyle.animation = "pulse 3s ease infinite";
        } else if (animation === "specialSpin") {
          buttonStyle.animation = "spin 10.8s linear infinite";
        }
      }
    } else {
      // Use standard variants
      variantClasses = VARIANTS[variant];
    }

    return (
      <Component
        className={cn(
          BASE_STYLES,
          variantClasses,
          SIZES[normalizedSize],
          animationClass,
          className,
        )}
        style={buttonStyle}
        ref={ref}
        disabled={disabled}
        {...props}
      >
        {children}
      </Component>
    );
  },
);

Button.displayName = "Button";

Button.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(["primary", "secondary", "outline", "ghost"]),
  size: PropTypes.oneOf(getAllValidSizes()),
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  as: PropTypes.elementType, // Prop type for 'as'
  gradient: PropTypes.bool, // Add gradient prop type
  gradientType: PropTypes.string, // Add gradientType prop type
  animation: PropTypes.string, // Add animation prop type
  style: PropTypes.object, // Add style prop type
};

// Memoize the button to prevent unnecessary rerenders
const MemoButton = memo(Button);
export default MemoButton;
export { MemoButton as Button };
